/**
 * establishSession: the ONE way a sign-in becomes a signed-in app.
 *
 * Password sign-in (pages/Login.tsx) and magic-link sign-in
 * (pages/MagicLogin.tsx) both get the same answer from the server
 * ({ token, user, source }) and both call this with it. It saves the token,
 * records who is signed in, asks the server what they may do, and says where
 * to go next.
 *
 * 2026-10-08: the magic-link page had its own copy of "save the session",
 * which wrote `access_token` / `refresh_token`, keys nothing in the app
 * reads (the app's key is `authToken`). A second copy of this logic is how
 * that happened, so there is no second copy: this was moved here from
 * Login.tsx unchanged.
 */
import type { Dispatch } from "redux";
import {
  setAuthzFromCapabilities,
  setIsMember,
  setIsStaff,
  setUser,
} from "../store/slices/authSlice";
import { getCapabilities } from "../api/authAPI";
import { getUserDetail } from "../api/usersAPI";
import { getMyOverdueActions } from "../api/membershipActionsAPI";
import { resolveStaffHomeRoute, staffHomeRoutePath } from "./staffHomeRoute";
import { clearUserData } from "./logout";
import { consumePostLoginDestination } from "./postLoginDestination";
import { reportSignInTokenNotSaved } from "../api/sessionDiagnostics";

/**
 * The previous session's keys. Removed BEFORE the new token is written
 * (2026-09-29, Gymnastics of York, reproduced on dev-tenant): writing on top
 * of the old values meant a full storage kept the OLD token — the server said
 * "signed in", the browser stayed on the weeks-old token, every reload sent
 * it, was refused, and staff were back at sign-in. Removing first frees the
 * room the new values need, and if a write still fails, no stale token is
 * left behind to loop on.
 */
const PREVIOUS_SESSION_KEYS = ["authToken", "authSource", "userRole", "userDetails", "overdueActions", "showOverdueActionsModal"];

function clearPreviousSession(): void {
  for (const key of PREVIOUS_SESSION_KEYS) {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  }
}

/**
 * Saved work that must never be dropped to make room: unsynced edits and
 * competition scores, a registration in progress, a cart — plus the club and
 * language the next screen needs.
 */
const NEVER_EVICT = [
  /^tenantSlug$/,
  /^language$/,
  /^clubcentral_offline_queue$/,
  /^competition_score_queue_v1:/,
  /^registration_wizard_state$/,
  /^cart$/,
];

/** Room the rest of sign-in needs after the token (profile, settings, …). */
const SIGN_IN_HEADROOM_CHARS = 64 * 1024;

function hasRoom(chars: number): boolean {
  try {
    localStorage.setItem("__cc_room_probe", "x".repeat(chars));
    localStorage.removeItem("__cc_room_probe");
    return true;
  } catch {
    try { localStorage.removeItem("__cc_room_probe"); } catch { /* ignore */ }
    return false;
  }
}

/** Write the sign-in token and confirm it is what storage now holds. */
function saveSignInToken(token: string): boolean {
  try {
    localStorage.setItem("authToken", token);
    return localStorage.getItem("authToken") === token;
  } catch {
    return false;
  }
}

/**
 * Last resort when the site's storage is full of something other than our
 * session: remove the largest entries (never NEVER_EVICT) one at a time until
 * the new token is saved AND the rest of sign-in has room. A full storage
 * otherwise means staff can never stay signed in on that browser.
 * Returns how many entries it removed, or -1 when it still could not save.
 */
function makeRoomAndSave(token: string): number {
  let candidates: Array<[string, number]> = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i) || "";
      if (!key || NEVER_EVICT.some((re) => re.test(key))) continue;
      candidates.push([key, (localStorage.getItem(key) || "").length]);
    }
  } catch {
    return -1;
  }
  candidates = candidates.sort((a, b) => b[1] - a[1]);
  let removed = 0;
  for (const [key] of candidates) {
    if (saveSignInToken(token) && hasRoom(SIGN_IN_HEADROOM_CHARS)) return removed;
    try { localStorage.removeItem(key); removed += 1; } catch { /* keep going */ }
  }
  return saveSignInToken(token) ? removed : -1;
}

/** Shown when the browser will not keep the new sign-in (storage or disk full). */
export const SIGN_IN_NOT_SAVED_MESSAGE =
  "You're signed in, but this browser couldn't save it, so you'd be signed straight back out. The browser's storage for this site is full, or the computer is out of disk space. Clear this site's browsing data, free up disk space, or try another browser.";

/** What the server answers a successful sign-in with. */
export interface SignInResponse {
  token: string;
  user?: any;
  source?: string;
  userId?: string;
}

export type EstablishedSession =
  /** `destination` is where this person's role lands; null = the caller's own default. */
  | { ok: true; role: string | undefined; destination: string | null }
  /** The browser would not keep the token. Show SIGN_IN_NOT_SAVED_MESSAGE; never continue. */
  | { ok: false; reason: "not_saved" };

// Normalize role for consistent comparison (handles spaces, underscores, case)
const normalizeRole = (role: string): string => role.toLowerCase().replace(/\s+/g, "_");

// Admin-equivalent roles (normalized)
const ADMIN_ROLES = new Set(["admin", "super_admin", "tenant_owner", "platform_admin"]);

// Check if role has admin privileges
const isAdminRole = (role: string | undefined): boolean => {
  if (!role) return false;
  return ADMIN_ROLES.has(normalizeRole(role));
};

// Helper to check if role is staff-like (staff, manager, junior_staff, senior_coach, etc.)
const isStaffRole = (role: string | undefined): boolean => {
  if (!role) return false;
  const nonStaffRoles = ["member", "customer", "kiosk"];
  // Not admin and not a non-staff role = staff
  return !isAdminRole(role) && !nonStaffRoles.includes(role);
};

/** Remember whether staff have overdue actions to be shown after sign-in. */
async function rememberOverdueActions(token: string): Promise<void> {
  try {
    const overdueRes = await getMyOverdueActions(token);
    if (overdueRes?.success && overdueRes?.data?.length > 0) {
      localStorage.setItem("showOverdueActionsModal", "true");
      localStorage.setItem("overdueActions", JSON.stringify(overdueRes.data));
    } else {
      localStorage.removeItem("showOverdueActionsModal");
      localStorage.removeItem("overdueActions");
    }
  } catch (error) {
    // Handled by axios interceptor
  }
}

export async function establishSession(
  data: SignInResponse,
  opts: { dispatch: Dispatch<any>; tenantId: string | null | undefined },
): Promise<EstablishedSession> {
  const { dispatch, tenantId } = opts;
  // Backend returns: { message, token, user, source }
  const { token, user, source } = data;

  // Store token and auth source (backend determines if central or tenant).
  // Confirm it stuck: a write that fails (storage full or blocked) leaves
  // the PREVIOUS token in place, and the very next request goes out with
  // it, gets refused, and signs the user straight back out. On failure,
  // report it, drop the app's cached data to free room, and try once
  // more before telling the user (sessionDiagnostics, 2026-09-29).
  clearPreviousSession();
  if (!saveSignInToken(token)) {
    reportSignInTokenNotSaved(token);
    try { clearUserData(); } catch { /* storage unavailable */ }
    if (!(saveSignInToken(token) && hasRoom(SIGN_IN_HEADROOM_CHARS)) && makeRoomAndSave(token) < 0) {
      // Nothing left to remove and it still will not save: the computer's
      // disk is full, or storage is blocked. Say so; never continue on an
      // unsaved sign-in.
      return { ok: false, reason: "not_saved" };
    }
  }
  if (source) {
    localStorage.setItem("authSource", source);
  }

  // Use user data from backend response
  let userData = user;
  if (!userData && data.userId) {
    const userResponse = await getUserDetail(token, data.userId, source);
    userData = userResponse?.data?.user;
  }

  dispatch(setUser(userData));
  const userRole = userData?.role?.toLowerCase();
  localStorage.setItem("userRole", userRole);
  localStorage.setItem("userDetails", JSON.stringify(userData));

  // Resolve staff/admin landing page from per-user override → tenant
  // default → 'dashboard' floor. userData carries both staff_home_route
  // and tenant_default_home_route from /api/auth/current-user.
  const resolvedStaffHome = resolveStaffHomeRoute(
    userData?.staff_home_route,
    userData?.tenant_default_home_route,
  );
  const staffHomeUrl = staffHomeRoutePath(resolvedStaffHome, tenantId || "");

  // Ask the server what this caller may actually do, before any routing.
  // isAdmin must never come from the role STRING: that reads users.role,
  // the drifted column the API does not authorize on. On production the
  // two disagreed for 5 of 14 staff accounts, in both directions.
  //
  // isAdminRole() below still decides the LANDING PAGE, which is a
  // navigation preference rather than a gate — being sent to the staff
  // home grants nothing the API would otherwise refuse.
  try {
    const caps = await getCapabilities();
    if (caps) dispatch(setAuthzFromCapabilities(caps));
  } catch {
    // Leave isAdmin false and let the backend be the authority. Failing
    // closed shows less than the user may do; failing open shows controls
    // that error on use.
  }

  // Role-based routing
  if (isAdminRole(userRole)) {
    // Check for overdue actions for staff/admin
    await rememberOverdueActions(token);
    return { ok: true, role: userRole, destination: staffHomeUrl };
  }
  if (isStaffRole(userRole)) {
    dispatch(setIsStaff(userRole));
    await rememberOverdueActions(token);
    return { ok: true, role: userRole, destination: staffHomeUrl };
  }
  if (userRole === "member" || userRole === "customer") {
    // Customers are member-portal accounts (see utils/roles isMemberRole);
    // their first enrollment auto-promotes them to member.
    dispatch(setIsMember(userRole));
    // An emailed link (e.g. "add a payment method") lands where it
    // pointed; everything else goes to the dashboard as before.
    return {
      ok: true,
      role: userRole,
      destination: consumePostLoginDestination(tenantId) || `/${tenantId}/dashboard`,
    };
  }
  // Default navigation is the caller's.
  return { ok: true, role: userRole, destination: null };
}
